import React from 'react';
import { typedAs } from 'utils';
import { CodeInputFormField } from 'components/codeInput';
import {
  FormInputTypes,
  GridColSpan,
  TypedFormInputs,
  UseFormMethods,
  UseInputs,
} from 'components/form';
import { CodeInputFormFieldProps } from 'components/codeInput/types';
import { PIN_LENGTH } from './utils';

export type ConfirmPinFormData = { pin: string };

const getFormInputs = (args?: {
  capturedPin: string;
}): TypedFormInputs<ConfirmPinFormData> => {
  const { capturedPin } = args ?? {};
  return {
    pin: {
      element: FormInputTypes.CustomInput,
      elementProps: {
        customInput: CodeInputFormField,
        ...typedAs<Omit<CodeInputFormFieldProps, 'name'>>({
          mask: true,
        }),
      },
      layout: GridColSpan.SpanFull,
      validation: {
        validate: (v: string): true | string => {
          if (!capturedPin) {
            return 'No PIN to confirm against.';
          }
          return v === capturedPin ? true : 'PINs do not match. Try again.';
        },
      },
    },
  };
};

export const getFormInputsHook =
  (args: {
    capturedPin: string;
    setIsValid: (isValid: boolean) => void;
  }): UseInputs<ConfirmPinFormData> =>
  (
    form: UseFormMethods<ConfirmPinFormData>,
  ): TypedFormInputs<ConfirmPinFormData> => {
    const { setIsValid, capturedPin } = args;
    const { watch } = form;
    const pin = watch('pin');

    React.useEffect(() => {
      if (pin?.length === PIN_LENGTH) {
        // trigger form validation on every change
        // once the PIN has reached the required length,
        form.trigger();
      }
    }, [pin]);

    const isValid = pin?.length === PIN_LENGTH && pin === capturedPin;
    React.useEffect(() => {
      setIsValid(isValid);
    }, [isValid]);

    return React.useMemo(() => getFormInputs({ capturedPin }), [capturedPin]);
  };

export const getFormDefaultValues = (): ConfirmPinFormData => {
  return {
    pin: '',
  };
};
